import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Property, PropertyImage, PropertyBrochure } from '../types';
import apiService from '../services/api';
import ImageGallery from '../components/ImageGallery';
import BrochureViewer from '../components/BrochureViewer';
import { 
  MapPin, 
  Home,
  Calendar,
  Share,
  Phone,
  Mail,
  MessageCircle
} from 'lucide-react';

export const PropertyDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [siteSettings, setSiteSettings] = useState<any>({});
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch both property and site settings
        const [propertyResponse, settingsResponse] = await Promise.all([
          apiService.getPropertyBySlug(slug!),
          apiService.getSiteSettings()
        ]);
        
        if (propertyResponse.data) {
          setProperty(propertyResponse.data);
        }
        
        if (settingsResponse.data) {
          setSiteSettings(settingsResponse.data);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to fetch data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]);

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setShowImageGallery(true);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: property?.title,
        text: property?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
    }
  };

  const handleWhatsApp = () => {
    const whatsappNumber = siteSettings.whatsapp_number || siteSettings.contact_phone || '+919867300996';
    const message = `Hi! I'm interested in the property: ${property?.title} located in ${property?.location}. Could you please provide more details? Property Link: ${window.location.href}`;
    const cleanNumber = whatsappNumber.replace(/[^0-9+]/g, '');
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactLoading(true);
    
    const formData = new FormData(e.currentTarget);
    const contactData = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      message: formData.get('message') as string,
      property_id: property?.id,
      property_title: property?.title,
      property_url: window.location.href
    };
    
    try {
      const response = await apiService.submitContactForm(contactData);
      
      if (response.success && response.data?.whatsapp_url) {
        // Open WhatsApp with the inquiry details
        window.open(response.data.whatsapp_url, '_blank');
        
        // Reset form
        e.currentTarget.reset();
        
        // Show success message
        alert('Your inquiry has been sent! We will contact you shortly.');
      } else {
        alert('Form submitted successfully! We will contact you soon.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    } finally {
      setContactLoading(false);
    }
  };

  const handleInquiry = () => {
    // Scroll to inquiry form or open modal
    const inquirySection = document.getElementById('inquiry-section');
    inquirySection?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Property not found</p>
          <a href="/" className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  const primaryImage = property.images?.find(img => img.is_primary) || property.images?.[0];
  const otherImages = property.images?.filter(img => !img.is_primary) || [];
  const allImages = property.images || [];

  return (
    <div className="min-h-screen bg-gray-50 mobile-optimized">
      {/* Hero Section with Images - Mobile Responsive & Professional */}
      <div className="relative bg-white shadow-sm">
        {/* Main Image Gallery */}
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-2 sm:gap-3 lg:gap-4 p-3 sm:p-4 h-[50vh] sm:h-[60vh] lg:h-[70vh]">
            {/* Primary Large Image - Mobile First */}
            <div className="col-span-12 lg:col-span-8 relative group cursor-pointer rounded-xl overflow-hidden shadow-lg" onClick={() => handleImageClick(0)}>
              <img
                src={primaryImage ? apiService.getImageUrl(primaryImage.file_path) : '/api/placeholder/800/600'}
                alt={property.title}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10 opacity-0 group-hover:opacity-100 transition-all duration-500" />
              
              {/* Mobile Gallery Button */}
              <div className="absolute bottom-4 left-4 lg:opacity-0 lg:group-hover:opacity-100 transition-all duration-300">
                <span className="bg-white/95 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold text-gray-900 shadow-md border border-white/20">
                  📸 View Gallery ({allImages.length} photos)
                </span>
              </div>

              {/* Mobile Swipe Indicator */}
              <div className="lg:hidden absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                  <div className="w-6 h-6 text-gray-700">👆</div>
                </div>
              </div>
            </div>
            
            {/* Desktop Thumbnail Grid */}
            <div className="hidden lg:block col-span-4">
              <div className="grid grid-cols-2 gap-3 h-full">
                {otherImages.slice(0, 6).map((image, index) => (
                  <div 
                    key={image.id} 
                    className="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                    onClick={() => handleImageClick(index + 1)}
                  >
                    <img
                      src={apiService.getImageUrl(image.file_path)}
                      alt={`${property.title} - ${index + 2}`}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                    
                    {/* Show remaining count on last thumbnail */}
                    {index === 5 && allImages.length > 7 && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center backdrop-blur-sm">
                        <div className="text-center">
                          <span className="text-white font-bold text-xl">+{allImages.length - 7}</span>
                          <p className="text-white/90 text-sm mt-1">More Photos</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Thumbnail Strip */}
            <div className="lg:hidden col-span-12 mt-2">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {otherImages.slice(0, 8).map((image, index) => (
                  <div 
                    key={image.id}
                    className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 relative cursor-pointer rounded-lg overflow-hidden shadow-md"
                    onClick={() => handleImageClick(index + 1)}
                  >
                    <img
                      src={apiService.getImageUrl(image.file_path)}
                      alt={`Thumbnail ${index + 2}`}
                      className="w-full h-full object-cover object-center"
                    />
                    {index === 7 && allImages.length > 9 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white font-bold text-xs">+{allImages.length - 9}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Share Button and Contact Icons - Mobile Optimized */}
        <div className="absolute top-3 sm:top-4 lg:top-6 right-3 sm:right-4 lg:right-6 flex flex-col gap-2 sm:gap-3">
          {/* Share Button */}
          <button
            onClick={handleShare}
            className="p-2 sm:p-3 bg-white/95 text-gray-700 rounded-full shadow-lg backdrop-blur-sm hover:bg-white hover:shadow-xl transition-all duration-300"
            title="Share Property"
          >
            <Share className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          
          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsApp}
            className="p-2 sm:p-3 bg-green-500 text-white rounded-full shadow-lg backdrop-blur-sm hover:bg-green-600 hover:shadow-xl transition-all duration-300"
            title={`WhatsApp: ${siteSettings.whatsapp_number || siteSettings.contact_phone || '+919867300996'}`}
          >
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          
          {/* Phone Button */}
          <a
            href={`tel:${siteSettings.contact_phone || '+919867300996'}`}
            className="p-2 sm:p-3 bg-blue-500 text-white rounded-full shadow-lg backdrop-blur-sm hover:bg-blue-600 hover:shadow-xl transition-all duration-300"
            title={`Call: ${siteSettings.contact_phone || '+919867300996'}`}
          >
            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
          </a>
          
          {/* Email Button */}
          <a
            href={`mailto:${siteSettings.contact_email || 'info@aaranhomes.com'}?subject=Inquiry about ${property?.title || 'Property'}`}
            className="p-2 sm:p-3 bg-red-500 text-white rounded-full shadow-lg backdrop-blur-sm hover:bg-red-600 hover:shadow-xl transition-all duration-300"
            title={`Email: ${siteSettings.contact_email || 'info@aaranhomes.com'}`}
          >
            <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
          </a>
        </div>
      </div>

      {/* Main Content - Mobile Responsive & Professional Layout */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 lg:py-12">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 lg:gap-8">
          {/* Property Information - Main Column */}
          <div className="xl:col-span-3 space-y-6 sm:space-y-8">
            {/* Property Header - Mobile Optimized */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">{property.title}</h1>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-6 text-sm sm:text-base text-gray-600">
                    <div className="flex items-center">
                      <div className="p-1.5 bg-blue-100 rounded-lg mr-3">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <span className="font-medium">{property.location}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="p-1.5 bg-blue-100 rounded-lg mr-3">
                        <Home className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <span className="capitalize">{property.property_type}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="p-1.5 bg-blue-100 rounded-lg mr-3">
                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <span>Listed {new Date(property.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                {/* Status Badge - Mobile Friendly */}
                <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
                  ✓ {property.status}
                </div>
              </div>
            </div>

            {/* Description Section - Professional Typography */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-3"></div>
                  About This Property
                </h2>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base sm:text-lg">
                    {property.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Property Details - Mobile Responsive Cards */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-3"></div>
                  Property Information
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                  <div className="space-y-4 sm:space-y-6">
                    <h3 className="font-bold text-gray-800 text-base sm:text-lg mb-3 sm:mb-4 pb-2 border-b border-gray-200">General Details</h3>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-50">
                        <span className="text-gray-600 font-medium mb-1 sm:mb-0">Property Type</span>
                        <span className="font-semibold text-gray-900 capitalize bg-gray-50 px-3 py-1 rounded-lg text-sm sm:text-base">{property.property_type}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-50">
                        <span className="text-gray-600 font-medium mb-1 sm:mb-0">Status</span>
                        <span className="font-semibold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg text-sm sm:text-base">{property.status}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-50">
                        <span className="text-gray-600 font-medium mb-1 sm:mb-0">Category</span>
                        <span className="font-semibold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg text-sm sm:text-base">{property.category?.name}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between py-3">
                        <span className="text-gray-600 font-medium mb-1 sm:mb-0">Location</span>
                        <span className="font-semibold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg text-sm sm:text-base">{property.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 sm:space-y-6">
                    <h3 className="font-bold text-gray-800 text-base sm:text-lg mb-3 sm:mb-4 pb-2 border-b border-gray-200">Additional Information</h3>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-50">
                        <span className="text-gray-600 font-medium mb-1 sm:mb-0">Featured Property</span>
                        <span className={`font-semibold px-3 py-1 rounded-lg text-sm sm:text-base ${
                          property.is_featured 
                            ? 'text-green-800 bg-green-50 border border-green-200' 
                            : 'text-gray-700 bg-gray-50'
                        }`}>
                          {property.is_featured ? '⭐ Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between py-3 border-b border-gray-50">
                        <span className="text-gray-600 font-medium mb-1 sm:mb-0">Property ID</span>
                        <span className="font-semibold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg text-sm sm:text-base font-mono">#{property.id}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between py-3">
                        <span className="text-gray-600 font-medium mb-1 sm:mb-0">Last Updated</span>
                        <span className="font-semibold text-gray-900 bg-gray-50 px-3 py-1 rounded-lg text-sm sm:text-base">{new Date(property.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Brochures Section - Mobile Optimized */}
            {property.brochures && property.brochures.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 sm:p-6 lg:p-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-3"></div>
                    Property Brochures
                  </h2>
                  <BrochureViewer brochures={property.brochures} />
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Mobile Responsive Contact Section */}
          <div className="xl:col-span-1 space-y-4 sm:space-y-6">
            {/* Contact Form - Professional & Mobile Optimized */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-4 sm:top-6" id="inquiry-section">
              <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 p-4 sm:p-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-purple-600/90"></div>
                <div className="relative">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1">Get In Touch</h3>
                  <p className="text-blue-100 text-sm sm:text-base">Interested in this property?</p>
                </div>
                <div className="absolute -bottom-2 -right-8 opacity-20">
                  <div className="w-24 h-24 rounded-full bg-white/10"></div>
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <form onSubmit={handleContactSubmit} className="space-y-4 sm:space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm sm:text-base"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm sm:text-base"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-sm sm:text-base"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 resize-none text-sm sm:text-base"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 sm:py-4 px-4 rounded-xl transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {contactLoading ? '📨 Sending...' : '📧 Send Inquiry'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Mobile CTA Button - Only visible on mobile */}
          <div className="xl:hidden fixed bottom-4 left-4 right-4 z-50">
            <button
              onClick={handleInquiry}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              💬 Contact About This Property
            </button>
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      {showImageGallery && (
        <ImageGallery
          images={allImages}
          initialIndex={selectedImageIndex}
          isOpen={showImageGallery}
          onClose={() => setShowImageGallery(false)}
        />
      )}
    </div>
  );
};